import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { KEY_CODES, REGULARS } from 'utils';
import { CloseIcon, EditIcon, TickIcon, TrashBinIcon } from 'components/icons';
import './input.styles.scss';

export interface EditInputProps {
	onChangeSubmit?: (id: number, v: string, inputName: string) => void;
	onDelete?: (id: number, shouldUpdated?: boolean) => void;
	value?: string;
	id: number;
	exampleList?: string[];
	name: string;
	isEditable?: boolean;
	isDeletable?: boolean;
	disabled?: boolean;
	wrapperClass?: string;
	focusOnMount?: boolean;
	maxLength?: number;
	error?: string;
	icon?: string;
	hideErrorMessage?: boolean;
}

export const EditInput = ({
	onChangeSubmit = () => {},
	isEditable = true,
	isDeletable,
	onDelete = () => {},
	exampleList = [],
	value,
	name,
	id,
	error,
	focusOnMount,
	maxLength,
	wrapperClass = '',
	icon = '',
	hideErrorMessage = false,
}: EditInputProps) => {
	const [isEditing, setIsEditing] = useState(!!focusOnMount);
	const [currentValue, setCurrentValue] = useState<string>();
	const [prevValue, setPrevValue] = useState<string>();

	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const [isNewCreatedExample, isValueChanged] = useMemo(
		() => [!currentValue && !prevValue, currentValue !== prevValue],
		[currentValue, prevValue],
	);

	const isSubmitAvailable = useMemo(
		() =>
			!isNewCreatedExample &&
			isValueChanged &&
			currentValue &&
			!exampleList.includes(currentValue),
		[exampleList, currentValue, isNewCreatedExample, isValueChanged],
	);

	useEffect(() => {
		setPrevValue(value);
		setCurrentValue(value);
	}, [value]);

	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isEditing]);

	function handleChangeSubmit() {
		setIsEditing(false);

		if (!currentValue) {
			handleDelete();
		}
		if (onChangeSubmit) {
			onChangeSubmit(id, currentValue as string, name);
		}
	}

	function handleChange({
		target: { value: newValue },
	}: React.ChangeEvent<HTMLInputElement>) {
		setCurrentValue(
			newValue.replace(REGULARS.exceptEngLettersDigitsSymbols, ''),
		);
	}

	useEffect(() => {
		error && setCurrentValue(prevValue);
	}, [error, prevValue]);

	function handleKeyUp(e: React.KeyboardEvent) {
		if (e.key === KEY_CODES.ENTER) {
			isSubmitAvailable && handleChangeSubmit();
		}
	}

	function onClickEdit() {
		setIsEditing(true);
	}

	function handleClose() {
		if (!prevValue) {
			onDelete(id);
		} else if (isValueChanged) {
			setCurrentValue(prevValue);
		}

		setIsEditing(false);
	}

	function handleDelete() {
		onDelete(id, true);
	}

	function onBlurContainer() {
		if (isNewCreatedExample) {
			onDelete(id);
			setIsEditing(false);
		} else if (!isValueChanged) {
			setIsEditing(false);
		}
	}
	return (
		<div
			className={`edit-input ${wrapperClass}`}
			ref={containerRef}
			onBlur={onBlurContainer}
		>
			{icon && (
				<img src={icon} className="edit-input__amazon-icon" alt="Role logo" />
			)}
			{isEditing ? (
				<>
					<input
						type="text"
						value={currentValue}
						onChange={handleChange}
						name={name}
						id={`${id}`}
						ref={inputRef}
						className="edit-input__input"
						onKeyUp={handleKeyUp}
						maxLength={maxLength}
					/>
					<CloseIcon
						iconClass="edit-input__icon edit-input__icon--close"
						onClick={handleClose}
					/>
					{isSubmitAvailable && (
						<button
							data-action="submit"
							type="button"
							onClick={handleChangeSubmit}
							className={classNames('edit-input__icon', {
								disabled: !currentValue,
							})}
							disabled={!currentValue}
						>
							<TickIcon
								iconClass={classNames(
									'edit-input__icon',
									'edit-input__icon--tick',
									{
										disabled: !currentValue,
									},
								)}
							/>
						</button>
					)}
				</>
			) : (
				<>
					<p className="edit-input__value">{currentValue}</p>
					{isDeletable && (
						<TrashBinIcon iconClass="edit-input__icon" onClick={handleDelete} />
					)}
					{isEditable && (
						<EditIcon iconClass="edit-input__icon" onClick={onClickEdit} />
					)}
				</>
			)}
			{!hideErrorMessage && (
				<p
					className={`group__hint font-caption ${
						error ? 'group__hint--error' : ''
					}`}
				>
					{error}
				</p>
			)}
		</div>
	);
};
